import React from 'react';
import {Box, Spinner, VStack} from '@chakra-ui/react';
import {useHalState} from '@synako/haligator';
import {Pagination} from '@/components/Pagination';
import {TPath, useTypedQuery} from '@synako/typed-path';
import {FiltresItemCatalogueGrandCompte} from 'ff-common';
import {LigneCatalogueGrandCompte} from './LigneCatalogueGrandCompte';
import {ArticleDesc} from 'api-types/ffconnect-hal';
import {Hit, SearchCatalogueState} from 'api-types/ffconnect';
import {FollowPromiseOne} from '@synako/halitrek';

// Générique sur le type de hit : le panneau d'ajout des opérations commerciales reçoit un hit
// enrichi (obsolescence, réservation) et le transmet tel quel à son renderer
interface SearchResultsProps<H extends Hit> {
  isLoading: boolean;
  hits: H[] | undefined;
  paginationData: SearchCatalogueState['pagination'] | undefined;
  followArticle: (
    code_article: string,
  ) => FollowPromiseOne<ArticleDesc> | undefined;
  filtersHook: ReturnType<
    typeof useTypedQuery<TPath<{Query: FiltresItemCatalogueGrandCompte}>>
  >;
  backoffice?: boolean;
  // Custom item pour les opérations commerciales
  customItemRenderer?: React.ComponentType<{
    hit: H;
    articleNav: FollowPromiseOne<ArticleDesc>;
    onAjouterArticle: (code_article: string) => void;
    isSubmitting?: boolean;
  }>;
  onAjouterArticle: (code_article: string) => void;
  isSubmitting?: boolean;
}

/**
 * Composant d'affichage des résultats de recherche pour le catalogue grand compte
 */
export const SearchResults = <H extends Hit>({
  isLoading,
  hits,
  paginationData,
  followArticle,
  filtersHook,
  backoffice = false,
  customItemRenderer: CustomItem,
  onAjouterArticle,
  isSubmitting,
}: SearchResultsProps<H>) => {
  const handlePageChange = (page: number) => {
    filtersHook.change('page', page);
  };

  return (
    <VStack
      p='4'
      spacing={0}
      minH={'50vh'}
    >
      {isLoading && <Spinner />}

      {isLoading
        ? 'Chargement...'
        : hits?.map((hit) => {
            const articleNav = followArticle(hit.code_article);
            if (!articleNav) return null;

            // Si aucun CustomItem n'est fourni, utiliser le composant par défaut
            if (!CustomItem) {
              return (
                <ChargementLigneCatalogueGrandCompte
                  key={hit.code_article}
                  hit={hit}
                  articleNav={articleNav}
                  backoffice={backoffice}
                />
              );
            }

            // Sinon, on passe directement articleNav au composant personnalisé
            return (
              <CustomItem
                hit={hit}
                articleNav={articleNav}
                key={hit.code_article}
                onAjouterArticle={onAjouterArticle}
                isSubmitting={isSubmitting}
              />
            );
          })}

      {/* Pagination footer */}
      {!isLoading && paginationData && paginationData.total_pages > 1 && (
        <Box
          w='100%'
          mt={4}
        >
          <Pagination
            pageActuelle={paginationData.page}
            totalPages={paginationData.total_pages}
            onPageChange={handlePageChange}
          />
        </Box>
      )}
    </VStack>
  );
};

const ChargementLigneCatalogueGrandCompte: React.FC<{
  hit: Hit;
  articleNav: FollowPromiseOne<ArticleDesc>;
  backoffice: boolean;
}> = ({hit, articleNav, backoffice}) => {
  const articleHook = useHalState(articleNav);

  return (
    <LigneCatalogueGrandCompte
      hit={hit}
      articleState={articleHook.state}
      backoffice={backoffice}
    />
  );
};
